import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import DrumTabViewer from "@/components/DrumTabViewer";
import VirtualDrumKit from "@/components/VirtualDrumKit";
import Metronome from "@/components/Metronome";
import AuthModal from "@/components/AuthModal";
import PricingModal from "@/components/PricingModal";
import TeamSheet from "@/components/TeamSheet";
import ReferralModal from "@/components/ReferralModal";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, AlertTriangle } from "lucide-react";

export default function PracticePack() {
  const { id } = useParams();
  const { user } = useAuth();
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(false);
  const [sel, setSel] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);
  const [metroOpen, setMetroOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const isPro = !!(user && user.is_pro);
  const handleUpgrade = () => (user ? setPricingOpen(true) : setAuthOpen(true));

  useEffect(() => {
    api.get(`/packs/${id}`).then((r) => {
      setPack(r.data);
      if (r.data.referral_code && !localStorage.getItem("beatflow_ref")) localStorage.setItem("beatflow_ref", r.data.referral_code);
    }).catch(() => setError(true));
  }, [id]);
  const logPractice = useCallback((secs, t) => {
    if (t && typeof t._packItem === "number") api.post(`/packs/${id}/practice`, { item: t._packItem, seconds: Math.round(secs) }).catch(() => {});
  }, [id]);
  const tab = useMemo(() => {
    const it = pack && pack.items[sel];
    if (!it) return null;
    const sec = it.tab.sections[it.section] || it.tab.sections[0];
    return { ...it.tab, sections: [sec], _sheet: true, _packItem: sel };
  }, [pack, sel]);

  return (
    <div className="min-h-screen bg-[#0b0c10] text-slate-100">
      <Header
        onOpenAuth={() => setAuthOpen(true)}
        onOpenMetronome={() => setMetroOpen(true)}
        onOpenLibrary={() => {}}
        onOpenPricing={() => setPricingOpen(true)}
        onOpenTeam={() => setTeamOpen(true)}
        onOpenReferral={() => setReferralOpen(true)}
        libraryCount={0}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#ffab00]/30 bg-[#ffab00]/5 text-xs font-tab tracking-widest uppercase amber-text">
            <Sparkles className="w-3.5 h-3.5" />
            Practice pack
          </div>
          <Link to="/">
            <Button className="bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold" data-testid="create-own-button">
              <Sparkles className="w-4 h-4 mr-2" /> Make your own
            </Button>
          </Link>
        </div>

        {error ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-[#FF3366]/70" />
            <h3 className="font-heading text-2xl uppercase tracking-tight">Practice pack not found</h3>
            <p className="text-slate-500 text-sm mt-2">This link is invalid or has been removed.</p>
          </div>
        ) : !pack || !tab ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading practice pack…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-3 rounded-2xl border border-[#ffab00]/25 bg-[#ffab00]/[0.04] p-5" data-testid="pack-header">
              <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight">{pack.caption || `${pack.teacher}'s practice pack`}</h1>
              <p className="text-sm text-slate-400 mt-1">{pack.items.length} part{pack.items.length > 1 ? "s" : ""} to practise{pack.caption && pack.teacher ? ` · from ${pack.teacher}` : ""}. Pick one, press play, and use slow-down or loop as needed.</p>
              {!user && (
                <button onClick={() => setAuthOpen(true)} className="mt-3 inline-flex items-center gap-2 h-8 px-4 rounded-full bg-[#00f0ff] text-black text-xs font-semibold hover:bg-[#33f4ff] transition-colors" data-testid="pack-signup-cta">
                  Sign up free to save your progress
                </button>
              )}
              <div className="flex flex-wrap gap-2 mt-4" data-testid="pack-items">
                {pack.items.map((it, i) => (
                  <button key={i} onClick={() => setSel(i)} className={`rounded-full border px-4 py-2 text-left text-xs transition-colors ${sel === i ? "border-[#00f0ff] bg-[#00f0ff]/10 text-[#00f0ff]" : "border-white/15 text-slate-300 hover:border-[#00f0ff]/40"}`} data-testid={`pack-item-${i}`}>
                    <span className="font-semibold">{i + 1}. {it.tab.title}</span>
                    <span className="text-slate-500"> · {(it.tab.sections[it.section] || {}).name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <DrumTabViewer tab={tab} user={user} isPro={isPro} onUpgrade={handleUpgrade} onRequireAuth={() => setAuthOpen(true)} onSaved={() => {}} onPracticeFlush={logPractice} />
            </div>
            <div className="lg:col-span-1">
              <VirtualDrumKit />
            </div>
          </div>
        )}
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Metronome open={metroOpen} onOpenChange={setMetroOpen} />
      <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} onRequireAuth={() => setAuthOpen(true)} />
      <TeamSheet open={teamOpen} onOpenChange={setTeamOpen} />
      <ReferralModal open={referralOpen} onOpenChange={setReferralOpen} />
    </div>
  );
}
